import { useCallback, useEffect, useMemo } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import {
  canonicalizeMarketingSearchParams,
  formatMarketingDate,
  marketingFiltersToDateRange,
  readMarketingFilters,
  updateMarketingSearchParams,
} from "../marketingFilters";

export const useMarketingFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = useMemo(() => dayjs(), []);
  const search = searchParams.toString();

  const filters = useMemo(() => readMarketingFilters(new URLSearchParams(search), initialDate), [initialDate, search]);
  const canonicalSearchParams = useMemo(
    () => canonicalizeMarketingSearchParams(new URLSearchParams(search), filters),
    [filters, search],
  );
  const canonicalSearch = canonicalSearchParams.toString();
  const dateRange = useMemo(() => marketingFiltersToDateRange(filters), [filters.from, filters.to]);

  useEffect(() => {
    if (search !== canonicalSearch) {
      setSearchParams(canonicalSearchParams, { replace: true });
    }
  }, [canonicalSearch, canonicalSearchParams, search, setSearchParams]);

  const updateFilters = useCallback(
    (updates: Parameters<typeof updateMarketingSearchParams>[1]) => {
      setSearchParams((currentSearchParams) => updateMarketingSearchParams(currentSearchParams, updates));
    },
    [setSearchParams],
  );

  const setDateRange = useCallback(
    (range: [Dayjs, Dayjs]) => {
      updateFilters({
        from: formatMarketingDate(range[0]),
        to: formatMarketingDate(range[1]),
      });
    },
    [updateFilters],
  );

  const setCampaign = useCallback((campaign: string | null) => updateFilters({ campaign }), [updateFilters]);
  const setRep = useCallback((rep: string | null) => updateFilters({ rep }), [updateFilters]);

  return { filters, dateRange, setDateRange, setCampaign, setRep };
};
